import { useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { CheckCircle } from '../../../components/Icons'
import { IParsedFeatures } from '../../../types/subscription'

import { formatNumber } from '../../../utils/numbersFormatting'
import './styles.css'

const napsterLogo = '/assets/images/naspter-view-logo.png'

interface PackCardProps {
  onCheckout?: (
    price_id: PackCardProps['priceId'],
    returnURL: string,
    cancelUrl: string,
  ) => void
  isAuthenticated: boolean
  priceId: string
  name: string
  units: number
  price: number
  description: string
  features?: string
  per?: string
  hours?: string
}

const extraQueryParams = {}

export function PackCard({
  priceId,
  name,
  price,
  units,
  // description,
  features,
  onCheckout,
  per,
  isAuthenticated,
}: PackCardProps) {
  const dollarsAndCents = (price / 100).toFixed(2).split('.')

  const [isProgressing, setProgressing] = useState(false)

  const auth = useAuth()

  const parsedFeatures = useMemo<IParsedFeatures>(
    () => JSON.parse(features ?? '[]') as IParsedFeatures,
    [features],
  )

  const handleClick = () => {
    setProgressing(true)
    if (auth.user) {
      const urlParams = new URLSearchParams(window.location.search)
      let nextUrl = urlParams.get('next_url')
      nextUrl =
        nextUrl === null || nextUrl === undefined || nextUrl === ''
          ? `${window.location.origin}/pricing`
          : nextUrl
      onCheckout?.(priceId, nextUrl, name)
      return
    }
    if (price === 0) {
      void auth.signinRedirect({
        extraQueryParams: { ...extraQueryParams, action: 'get-started' },
      })
      return
    }

    const fullPath = `/pricing-checkout?priceId=${priceId}`
    void auth.signinRedirect({
      state: {
        path: fullPath,
        returnUrl: '/pricing',
      },
      extraQueryParams: {
        ...extraQueryParams,
        action: 'get-started',
      },
    })
  }

  return (
    <div className={`pricing-card-container pack `}>
      <div
        className={`pricing-card py-6 px-6 ${
          parsedFeatures?.isPopular ? 'tc-mv-blur-brown' : 'tc-mv-blur'
        }`}
      >
        <div className="flex gap-6 flex-wrap">
          <div className="sm:w-1/2 w-full">
            <h3 className="pricing-card-name">
              <div className="flex items-center font-[NapsterSans]">
                <span className="font-['DM_Serif_Text']">
                  {formatNumber(units)} minutes
                </span>
              </div>
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ${
                  parsedFeatures?.isPopular
                    ? 'sm:block hidden mb-[-20px]'
                    : 'hidden'
                }`}
              />
            </h3>
            <div className="pricing-card-price my-5 sm:my-2 mb-0 ">
              <span className="price">
                ${dollarsAndCents[0]}
                <sup className="text-[23px]">.{dollarsAndCents[1]}</sup>
              </span>
              {!!per && <span className="period">/{per}</span>}
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ml-auto ${
                  parsedFeatures?.isPopular ? 'block sm:hidden' : 'hidden'
                }`}
              />
            </div>
          </div>
          <div className="items-center sm:flex hidden">
            <ul className="pricing-card-features">
              {parsedFeatures?.items?.map((feature, index) => (
                <li key={index} className="flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {feature.html ? (
                      <span
                        className="text-sm text-white w-full"
                        dangerouslySetInnerHTML={{ __html: feature.html }}
                      />
                    ) : (
                      <span className="">{feature.text}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {/* <p className="pricing-card-description pack">{description}</p> */}
        <div className="items-center flex sm:hidden mt-4">
          <ul className="pricing-card-features">
            {parsedFeatures?.items?.map((feature, index) => (
              <li key={index} className="flex flex-col gap-2">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {feature.html ? (
                    <span
                      className="text-sm text-white w-full"
                      dangerouslySetInnerHTML={{ __html: feature.html }}
                    />
                  ) : (
                    <span className="">{feature.text}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <button
        className="pricing-card-button text-black bg-white"
        onClick={handleClick}
      >
        <span className="price-label p-2 text-black">
          {isAuthenticated ? 'Buy Now' : 'Get Started'}
        </span>
        {isProgressing ? (
          <AnimatedSpinner className="text-black bg-white" />
        ) : (
          ''
        )}
      </button>
    </div>
  )
}
